import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Textarea } from 'src/@/components/ui/textarea';
import { TIPOS_IMOVEL } from './perguntas/tiposImovel';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Agrupa tipos do catálogo por destino (residencial/comercial/ambos) para a UI.
const TIPOS_POR_DESTINO = TIPOS_IMOVEL.reduce((acc, t) => {
    (acc[t.destino] = acc[t.destino] || []).push(t);
    return acc;
}, {});

function TiposPicker({ selected, onChange }) {
    const { t } = useTranslation('settings');
    const toggle = (id) => {
        onChange(selected.includes(id) ? selected.filter((tipo) => tipo !== id) : [...selected, id]);
    };

    const selectAll = () => onChange(TIPOS_IMOVEL.map((tipo) => tipo.id));
    const clear = () => onChange([]);

    const Group = ({ label, items }) => (
        <div className="flex flex-col gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
            <div className="flex flex-wrap gap-2">
                {items.map((tipo) => {
                    const active = selected.includes(tipo.id);
                    return (
                        <button
                            key={tipo.id}
                            type="button"
                            onClick={() => toggle(tipo.id)}
                            className={`transition-all active:transform active:scale-95 ${active ? '' : 'opacity-70 hover:opacity-100'}`}
                        >
                            <Badge
                                variant="outline"
                                className={`text-xs font-medium flex items-center gap-1.5 cursor-pointer ${
                                    active
                                        ? 'bg-violet-50 text-violet-700 border-violet-200'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-violet-200'
                                }`}
                            >
                                <Iconify icon={tipo.icon} width={14} className={active ? 'text-violet-600' : 'text-gray-400'} />
                                {tipo.label}
                            </Badge>
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <p className={HINT_CLS}>
                    {selected.length === 0
                        ? t('aiAgents.realstate.escopo.tiposNoneSelected', 'Nenhum selecionado — Lais considera que atende todos os tipos.')
                        : t('aiAgents.realstate.escopo.tiposSelectedCount', '{{count}} tipo(s) selecionado(s).', { count: selected.length })}
                </p>
                <div className="flex gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={selectAll} className="text-xs rounded-lg text-[#7C3AED] hover:bg-violet-50">
                        {t('aiAgents.realstate.escopo.tiposSelectAll', 'Selecionar todos')}
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={clear} className="text-xs rounded-lg text-gray-500 hover:bg-gray-50" disabled={!selected.length}>
                        {t('aiAgents.realstate.escopo.tiposClear', 'Limpar')}
                    </Button>
                </div>
            </div>
            {TIPOS_POR_DESTINO.residencial && <Group label={t('aiAgents.realstate.escopo.tiposGroupResidencial', 'Residencial')} items={TIPOS_POR_DESTINO.residencial} />}
            {TIPOS_POR_DESTINO.comercial && <Group label={t('aiAgents.realstate.escopo.tiposGroupComercial', 'Comercial / Industrial')} items={TIPOS_POR_DESTINO.comercial} />}
            {TIPOS_POR_DESTINO.ambos && <Group label={t('aiAgents.realstate.escopo.tiposGroupAmbos', 'Ambos')} items={TIPOS_POR_DESTINO.ambos} />}
        </div>
    );
}

// Aceita apenas inteiros >= 0; string vazia → undefined (para cair no default/sem limite).
const coerceMoney = (value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return undefined;
    return Math.trunc(n);
};

export default function EscopoSection({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const escopo = aiAgent?.config?.module?.realstate?.escopo || {};
    const tiposAtendidos = Array.isArray(escopo.tiposAtendidos) ? escopo.tiposAtendidos : [];

    const updateEscopo = (patch) => {
        changeModuleConfig({ escopo: { ...escopo, ...patch } });
    };

    return (
        <div className="flex flex-col gap-4">
            {/* ─── Tipos de imóvel atendidos ─────────────── */}
            <Accordion type="single" collapsible className="rounded-xl border border-gray-100 bg-white">
                <AccordionItem value="tipos-imovel" className="border-0">
                    <AccordionTrigger className="hover:no-underline px-6 py-4 [&[data-state=open]]:pb-3">
                        <div className="flex items-center gap-3 flex-1 pr-3 text-left">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 shrink-0">
                                <Iconify icon="mdi:home-group" width={20} className="text-violet-600" />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-gray-900 leading-tight">{t('aiAgents.realstate.escopo.tiposImovelTitle', 'Tipos de imóvel atendidos')}</div>
                                <div className="text-sm text-gray-500 font-normal mt-1">
                                    {t('aiAgents.realstate.escopo.tiposImovelSubtitle', 'Selecione apenas os tipos que sua imobiliária trabalha. A Lais se orienta por essa lista antes de oferecer opções.')}
                                </div>
                            </div>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                        <TiposPicker
                            selected={tiposAtendidos}
                            onChange={(tipos) => updateEscopo({ tiposAtendidos: tipos })}
                        />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {/* ─── Faixas de valor praticadas ──────────────── */}
            <Accordion type="single" collapsible className="rounded-xl border border-gray-100 bg-white">
                <AccordionItem value="faixas-valor" className="border-0">
                    <AccordionTrigger className="hover:no-underline px-6 py-4 [&[data-state=open]]:pb-3">
                        <div className="flex items-center gap-3 flex-1 pr-3 text-left">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50 shrink-0">
                                <Iconify icon="mdi:cash-multiple" width={20} className="text-emerald-600" />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-gray-900 leading-tight">{t('aiAgents.realstate.escopo.faixaValorTitle', 'Faixas de valor praticadas')}</div>
                                <div className="text-sm text-gray-500 font-normal mt-1">
                                    {t('aiAgents.realstate.escopo.faixaValorSubtitle', 'Valores mínimo e máximo do catálogo. Quando o cliente pedir fora da faixa, a Lais esclarece antes de buscar.')}
                                </div>
                            </div>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                        <div className="flex flex-col gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-2">
                                    <Label className={LABEL_CLS}>{t('aiAgents.realstate.escopo.aluguelMinLabel', 'Aluguel mínimo (R$)')}</Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={escopo.aluguelMin ?? ''}
                                        onChange={(e) => updateEscopo({ aluguelMin: coerceMoney(e.target.value) })}
                                        placeholder={t('aiAgents.realstate.escopo.aluguelMinPlaceholder', 'Ex.: 800')}
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Label className={LABEL_CLS}>{t('aiAgents.realstate.escopo.aluguelMaxLabel', 'Aluguel máximo (R$)')}</Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={escopo.aluguelMax ?? ''}
                                        onChange={(e) => updateEscopo({ aluguelMax: coerceMoney(e.target.value) })}
                                        placeholder={t('aiAgents.realstate.escopo.aluguelMaxPlaceholder', 'Ex.: 15000')}
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Label className={LABEL_CLS}>{t('aiAgents.realstate.escopo.vendaMinLabel', 'Venda mínimo (R$)')}</Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={escopo.vendaMin ?? ''}
                                        onChange={(e) => updateEscopo({ vendaMin: coerceMoney(e.target.value) })}
                                        placeholder={t('aiAgents.realstate.escopo.vendaMinPlaceholder', 'Ex.: 150000')}
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Label className={LABEL_CLS}>{t('aiAgents.realstate.escopo.vendaMaxLabel', 'Venda máximo (R$)')}</Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={escopo.vendaMax ?? ''}
                                        onChange={(e) => updateEscopo({ vendaMax: coerceMoney(e.target.value) })}
                                        placeholder={t('aiAgents.realstate.escopo.vendaMaxPlaceholder', 'Ex.: 2500000')}
                                        className={INPUT_CLS}
                                    />
                                </div>
                            </div>
                            <p className={HINT_CLS}>{t('aiAgents.realstate.escopo.faixaValorHint', 'Deixe em branco quando não houver limite (aberto em cima ou embaixo).')}</p>
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {/* ─── Assuntos fora do escopo ──────────────────── */}
            <Accordion type="single" collapsible className="rounded-xl border border-gray-100 bg-white">
                <AccordionItem value="assuntos-fora" className="border-0">
                    <AccordionTrigger className="hover:no-underline px-6 py-4 [&[data-state=open]]:pb-3">
                        <div className="flex items-center gap-3 flex-1 pr-3 text-left">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-50 shrink-0">
                                <Iconify icon="mdi:alert-octagon-outline" width={20} className="text-amber-600" />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-gray-900 leading-tight">{t('aiAgents.realstate.escopo.assuntosForaTitle', 'Assuntos fora do escopo')}</div>
                                <div className="text-sm text-gray-500 font-normal mt-1">
                                    {t('aiAgents.realstate.escopo.assuntosForaSubtitle', 'Quando o cliente trouxer um desses assuntos, a Lais redireciona para o consultor humano em vez de tentar responder.')}
                                </div>
                            </div>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                        <div className="flex flex-col gap-2">
                            <Label className={LABEL_CLS}>{t('aiAgents.realstate.escopo.assuntosForaLabel', 'Tópicos que não devem ser tratados pela Lais')}</Label>
                            <Textarea
                                value={escopo.assuntosForaEscopo || ''}
                                onChange={(e) => updateEscopo({ assuntosForaEscopo: e.target.value })}
                                placeholder={t('aiAgents.realstate.escopo.assuntosForaPlaceholder', 'Ex.:\n- Avaliação de imóvel para venda\n- Questões judiciais e contratos jurídicos\n- Reclamações administrativas já em andamento\n- Imóveis que não constam no nosso catálogo')}
                                rows={5}
                                className={INPUT_CLS}
                            />
                            <p className={HINT_CLS}>{t('aiAgents.realstate.escopo.assuntosForaHint', 'Um tópico por linha. A Lais usa essa lista como guardrail e encaminha ao humano quando aparecer.')}</p>
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
}
